'use client';

import { useState } from 'react';
import { Input, Button, Textarea } from '~/components/primitives';
import { graphql } from '@funkey/types/graphql/gql';
import FormInputErrors from '~/components/primitives/form/FormInputErrors';
import FormGroup from '~/components/primitives/form/FormGroup';
import { z } from 'zod';
import useFormState, { createFormValidation } from 'use-form-state';
import { useI18n } from '~/features/i18n';
import { api } from '~/modules';
import Dropzone from '~/features/fileUpload/Dropzone';
import Title from '~/components/utils/Title';
import useNotifications from '~/features/notifications/useNotifications';
import { useReCAPTCHA } from '~/features/form/recaptcha/client';
import type { StoryblokBlokProps } from './utils';
import type { SB_ApplicationFormSectionProps } from './types.generated';

const SEND_EMAIL_MUTATION = graphql(`
    mutation sendEmail($input: SendEmailInput!) {
        sendEmail(input: $input) {
            success
        }
    }
`);

export const ApplicationFormSection: React.FC<
    StoryblokBlokProps<SB_ApplicationFormSectionProps>
> = props => {
    const recaptcha = useReCAPTCHA();
    const formState = useApplicationFormSectionFormState();
    const [resetCounter, setResetCounter] = useState(0);
    const notifications = useNotifications();
    const i18n = useI18n();

    const sendEmail = api.useGraphqlMutation(SEND_EMAIL_MUTATION, {
        throwOnError: false,
        onSuccess(data) {
            if (data.sendEmail?.success) {
                notifications.dispatch({
                    type: 'SUCCESS',
                    title: i18n._('Success'),
                    message: i18n._('contact.email.success'),
                });

                formState.resetForm();
                setResetCounter(prev => prev + 1);
            } else {
                notifications.dispatch({
                    type: 'ERROR',
                    title: i18n._('Error'),
                    message: i18n._('contact.email.error'),
                });
            }
        },
        onError(error) {
            // eslint-disable-next-line no-console
            console.error(error);
            notifications.dispatch({
                type: 'ERROR',
                title: i18n._('Error'),
                message: i18n._('contact.email.error'),
            });
        },
    });

    return (
        <section
            id={props.blok.id}
            className="flex w-full justify-center bg-brown-100 px-6 pb-36 pt-5"
        >
            <form action="">
                <div className="w-full rounded-4xl bg-white px-9 py-12 md:p-20 lg:min-w-[960px]">
                    <Title size="xl">{props.blok.title}</Title>

                    <div className="mt-10 flex w-full flex-col gap-2 lg:flex-row lg:gap-6">
                        <FormGroup
                            label={i18n._('First name')}
                            htmlFor="firstname"
                            className="mt-1 w-full"
                        >
                            <Input id="firstname" {...formState.getNativeInputProps('firstname')} />
                            <FormInputErrors errors={formState.getErrorMessages('firstname')} />
                        </FormGroup>

                        <FormGroup
                            label={i18n._('Last name')}
                            htmlFor="lastname"
                            className="mt-1 w-full"
                        >
                            <Input id="lastname" {...formState.getNativeInputProps('lastname')} />
                            <FormInputErrors errors={formState.getErrorMessages('lastname')} />
                        </FormGroup>
                    </div>

                    <div className="mt-2 flex w-full flex-col gap-2 lg:mt-8 lg:flex-row lg:gap-6">
                        <FormGroup label={i18n._('Email')} htmlFor="email" className="mt-1 w-full">
                            <Input id="email" {...formState.getNativeInputProps('email')} />
                            <FormInputErrors errors={formState.getErrorMessages('email')} />
                        </FormGroup>

                        <FormGroup
                            label={i18n._('Phone number')}
                            htmlFor="phonenumber"
                            className="mt-1 w-full"
                        >
                            <Input
                                id="phonenumber"
                                {...formState.getNativeInputProps('phonenumber')}
                            />
                            <FormInputErrors errors={formState.getErrorMessages('phonenumber')} />
                        </FormGroup>
                    </div>

                    <div className="mt-2 lg:mt-8">
                        <FormGroup label={i18n._('Upload CV & cover letter')} htmlFor="cv">
                            <span className="font-medium text-primary-400">
                                {i18n._('.pdf .doc or .docx with a max file size of 5mb')}
                            </span>
                            <Dropzone
                                key={resetCounter}
                                initialFiles={formState.values.mediaHeader}
                                type="activity/header"
                                typeId={Number('')}
                                maxFiles={1}
                                onChange={files => formState.handleChange('mediaHeader', files)}
                                mimeTypes={['image/jpeg', 'image/png']}
                            />
                            <FormInputErrors errors={formState.getErrorMessages('mediaHeader')} />
                        </FormGroup>
                    </div>

                    <div className="mt-2 lg:mt-8">
                        <FormGroup
                            label={
                                <>
                                    <span className="font-medium text-primary-900">
                                        {i18n._('Introduce yourself briefly')}
                                    </span>
                                    <span className="font-medium text-primary-400">
                                        {' '}
                                        ({i18n._('max 5000 characters')})
                                    </span>
                                </>
                            }
                            htmlFor="message"
                            className="mt-1 w-full"
                        >
                            <Textarea
                                className="mt-1"
                                value={formState.values.message}
                                rows={5}
                                onChange={e =>
                                    formState.handleChange('message', e.currentTarget.value)
                                }
                                placeholder={i18n._('contact.form.message.placeholder')}
                            />
                            <FormInputErrors errors={formState.getErrorMessages('message')} />
                        </FormGroup>
                    </div>

                    <div className="col-span-2">
                        <recaptcha.Component />
                    </div>

                    <div className="mt-10">
                        <Button
                            onClick={async () => {
                                if (!formState.validate()) return;

                                await sendEmail.mutateAsync({
                                    input: {
                                        subject: i18n._('Application form'),
                                        firstName: formState.values.firstname,
                                        email: formState.values.email,
                                        body: [
                                            createHtmlLine(
                                                i18n._('contact.form.first_name'),
                                                formState.values.firstname,
                                            ),
                                            createHtmlLine(
                                                i18n._('contact.form.last_name'),
                                                formState.values.lastname,
                                            ),
                                            createHtmlLine(
                                                i18n._('contact.form.phone_number'),
                                                formState.values.phonenumber,
                                            ),
                                            createHtmlLine(
                                                i18n._('contact.form.message'),
                                                formState.values.message,
                                            ),
                                        ].join('<br>'),
                                        attachments: formState.values.mediaHeader,
                                    },
                                });
                            }}
                            variant="tertiary"
                            disabled={!recaptcha.isValidated}
                            iconBefore="arrow-right"
                        >
                            {i18n._('Send application')}
                        </Button>
                    </div>
                </div>
            </form>
        </section>
    );
};

/** Since we don't have templates for all the email, this is just a simple helper function to create a line of html */
export const createHtmlLine = (key: string, value: string | string[]) =>
    value ? `<strong>${key}</strong>: ${Array.isArray(value) ? value.join(', ') : value}` : null;

const NAME_VALIDATOR = z.string().min(1).max(256).trim();
function useApplicationFormSectionFormState() {
    const i18n = useI18n();

    const validation = createFormValidation([
        {
            path: 'firstname',
            validate: value => NAME_VALIDATOR.safeParse(value).success,
            message: i18n._('Firstname required and must be between {min} and {max} characters', {
                min: NAME_VALIDATOR.minLength,
                max: NAME_VALIDATOR.maxLength,
            }),
        },
        {
            path: 'lastname',
            validate: value => NAME_VALIDATOR.safeParse(value).success,
            message: i18n._('Lastname required and must be between {min} and {max} characters', {
                min: NAME_VALIDATOR.minLength,
                max: NAME_VALIDATOR.maxLength,
            }),
        },
        {
            path: 'email',
            validate: value => z.email().safeParse(value).success,
            message: i18n._('Invalid email'),
        },
        {
            path: 'phonenumber',
            validate: value =>
                z
                    .string()
                    .regex(/^\+?\d{8,}$/)
                    .safeParse(value).success,
            message: i18n._('Invalid phone number'),
        },
        {
            path: 'message',
            validate: value => z.string().min(1).safeParse(value).success,
            message: i18n._('Message must contain minimum 1 character'),
        },
        {
            path: 'mediaHeader',
            validate: value => z.array(z.object({})).safeParse(value).success,
            message: i18n._('Media header is required'),
        },
    ]);

    return useFormState(
        {
            firstname: '',
            lastname: '',
            email: '',
            phonenumber: '',
            message: '',
            mediaHeader: [],
        },
        {
            validation,
            valuesToInput: ({ firstname, lastname, email, phonenumber, message, mediaHeader }) => ({
                firstname,
                lastname,
                email,
                phonenumber,
                message,
                mediaHeader,
            }),
        },
    );
}
